import React, { useState, useEffect, useRef } from 'react';
import { X, RotateCcw, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { useResendEmail } from '../../../hooks/useReservationComms';
import { useToast } from '../../../contexts/ToastContext';

function ResendConfirmModal({ reservationId, job, onClose }) {
  const resendMutation = useResendEmail(reservationId);
  const toast = useToast();
  const [isResending, setIsResending] = useState(false);
  const [showPayload, setShowPayload] = useState(false);
  const modalRef = useRef(null);
  const firstFocusableRef = useRef(null);

  // Focus trap
  useEffect(() => {
    if (firstFocusableRef.current) {
      firstFocusableRef.current.focus();
    }
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleResend = async () => {
    if (isResending) return;

    setIsResending(true);
    try {
      await resendMutation.mutateAsync(job.id);
      toast.success('Email queued for immediate delivery');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to resend email');
      setIsResending(false);
    }
  };

  // Compute new dedupe key
  const baseDedupeKey = job.dedupe_key ? job.dedupe_key.replace(/:resend-\d+$/, '') : 'unknown';
  const existingResendMatch = job.dedupe_key?.match(/:resend-(\d+)$/);
  const nextResendNumber = existingResendMatch ? parseInt(existingResendMatch[1], 10) + 1 : 1;
  const newDedupeKey = `${baseDedupeKey}:resend-${nextResendNumber}`;

  const originalSendDate = new Date(job.send_at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  const isTemplateDeleted = !job.template_name;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-panel resend-modal"
        onClick={(e) => e.stopPropagation()}
        ref={modalRef}
      >
        <div className="modal-header">
          <h2>Resend Email</h2>
          <button
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close"
            ref={firstFocusableRef}
          >
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          <div className="resend-info-box">
            <p>
              This will send another copy of this email using the <strong>original data</strong> from
              when it was first sent.
            </p>
          </div>

          {isTemplateDeleted && (
            <div className="resend-warning">
              <AlertTriangle size={16} />
              <span>
                The template for this email has been deleted. The email will still send using the
                saved payload snapshot.
              </span>
            </div>
          )}

          <div className="resend-field">
            <label>Template:</label>
            <div>{job.template_name || <em>Deleted Template</em>}</div>
          </div>

          <div className="resend-field">
            <label>Original Send Date:</label>
            <div>{originalSendDate}</div>
          </div>

          <div className="resend-field">
            <label>Recipient:</label>
            <div>{job.recipient_email || 'Unknown'}</div>
          </div>

          <div className="resend-field">
            <label>Subject:</label>
            <div>{job.rendered_subject || job.subject_tpl || 'No subject'}</div>
          </div>

          <div className="resend-field">
            <label>New Dedupe Key:</label>
            <div className="resend-dedupe-key">{newDedupeKey}</div>
          </div>

          {job.payload_snapshot && (
            <div className="resend-payload-section">
              <button
                className="resend-payload-toggle"
                onClick={() => setShowPayload(!showPayload)}
              >
                {showPayload ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                <span>Payload Snapshot</span>
              </button>
              {showPayload && (
                <pre className="resend-payload-content">
                  {JSON.stringify(job.payload_snapshot, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose} disabled={isResending}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleResend}
            disabled={isResending}
          >
            {isResending ? (
              <>Resending...</>
            ) : (
              <>
                <RotateCcw size={16} />
                Resend Email
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ResendConfirmModal;
